import cloneDeep from 'lodash/cloneDeep'
import { AnyAction, combineReducers, Reducer } from 'redux'
import {
    createTransform,
    type PersistConfig,
    persistReducer,
} from 'redux-persist'
import storage from 'redux-persist/es/storage'

import { FILTER_SETTINGS, FILTERS, LOCAL_FILTERS } from 'const/reducerKeys'
import { PageId } from 'routing/pages'
import { GenericObject } from 'types'
import { deepOverride, deepPick } from 'utilities/reducers'

import auth from './auth'
import autoMerge from './autoMerge'
import cache from './cache'
import orgs from './orgs'
import { buildReducer } from './ui'
import {
    DynamicReducerKey,
    DynamicReducerOptions,
    getAllAsyncReducerKeys,
    getDefaultState,
    injectUiReducer,
} from './ui/ui'

// For these pages, we want to persist the filters. Both summary pages and
// entity detail pages are included so a saved filter set survives a reload:
// the filter-set name lives in filtersSettings (always persisted) while its
// applied field/operator/value lives in filters/localFilters, so detail pages
// must persist those too or the reloaded pill renders empty and drops out of
// the data request.
export const uiReducerFilterPersistentKeys: PageId[] = [
    'adAccountsSummaryPage',
    'campaignsSummaryPage',
    'dspSummaryPage',
    'portfoliosSummaryPage',
    'productsSummaryPage',
    'sovKeywordsSummaryPage',
    'targetingSummaryPage',
    'adAccountPage',
    'adGroupPage',
    'campaignPage',
    'dspAdvertiserPage',
    'dspLineItemPage',
    'dspOrderPage',
    'keywordPage',
    'portfolioPage',
    'productAdPage',
    'productPage',
    'rulebookPage',
] as const

// Tab names in the product catalog page
// Avoiding referencing tabs in the module to avoid adding a dependency in the main module
const PRODUCT_CATALOG_PAGE_TAB_KEYS: string[] = [
    'productCatalogAllProducts',
    'productCatalogSc',
    'productCatalogVcSourcing',
    'productCatalogVcManufacturing',
]

const EXECUTIVE_HOME_PAGE_TAB_KEYS: string[] = ['summary']

// For these dynamic module locations, we want to persist the filters
const dynamicReducerFilterPersistentKeys: string[] = [
    ...PRODUCT_CATALOG_PAGE_TAB_KEYS,
    ...EXECUTIVE_HOME_PAGE_TAB_KEYS,
]

// Dynamic detail-page modules whose filters should survive a reload. These
// store per-tab filters as top-level (spread) keys via combineChildrenReducers,
// so rather than enumerate collision-prone tab names in the shared list above,
// their own persist config keeps filters for every key in the module subtree.
export const dynamicReducerFilterPersistentPages: DynamicReducerKey[] = [
    'categoryDetailsPage',
    'labelPage',
    'segmentPage',
    'sovKeywordPage',
]

const appPersistedKeys: string[] = ['chartColorsMap', 'filters']

const keysToHydrateAnchoredFilters: PageId[] = [
    'campaignPage',
    'targetingSummaryPage',
]

const uiCommonPersistedKeys = ['tableSettings', FILTER_SETTINGS, 'hydrated']

interface UiKeyTransformationConfig {
    includeFilters: (key: string | number | symbol) => boolean
}

export const buildUiKeyTransformations = ({
    includeFilters,
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
}: UiKeyTransformationConfig) =>
    createTransform(
        (inboundState, key) => {
            let keysToStore = [...uiCommonPersistedKeys]
            if (includeFilters(key)) {
                // Applied filter values live in localFilters (the shared filter
                // bar edits localFilters and reads a combined view); filters is
                // often empty, so persisting both is required for a filter to
                // survive a reload.
                keysToStore = [...keysToStore, FILTERS, LOCAL_FILTERS]
            }
            if (key.toString() === 'app') {
                keysToStore = [...keysToStore, ...appPersistedKeys]
            }
            return deepPick(inboundState, keysToStore)
        },
        (outboundState, key) => {
            if (
                (keysToHydrateAnchoredFilters as string[]).includes(
                    key.toString()
                )
            ) {
                return deepOverride(
                    outboundState,
                    cloneDeep(getDefaultState() as any)[key],
                    'anchored'
                )
            }

            return outboundState
        }
    )

const uiPersistConfig = {
    key: 'ui',
    storage,
    stateReconciler: autoMerge,
    transforms: [
        buildUiKeyTransformations({
            includeFilters: (k) =>
                (uiReducerFilterPersistentKeys as string[]).includes(
                    k.toString()
                ),
        }),
    ],
    blacklist: [...getAllAsyncReducerKeys()],
}

const authPersistConfig = {
    key: 'auth',
    storage,
    stateReconciler: autoMerge,
    whitelist: ['userIsImpersonating', 'organizationId', 'organizationGroupId'],
}

export const injectReducer = (
    key: DynamicReducerKey,
    asyncReducer: Reducer<any, AnyAction>,
    options: DynamicReducerOptions = {}
): void => {
    const persistConfig: PersistConfig<GenericObject> = {
        key: `ui.${key}`,
        storage,
        stateReconciler: autoMerge,
        transforms: [
            buildUiKeyTransformations({
                includeFilters: (k) => {
                    if (dynamicReducerFilterPersistentPages.includes(key)) {
                        return true
                    }
                    return dynamicReducerFilterPersistentKeys.includes(
                        String(k)
                    )
                },
            }),
        ],
        blacklist: [
            'resource',
            'resourceParams',
            'mounting',
            'error',
            'updating',
            'widgets',
            'localFilters',
            'hydrated',
        ],
    }

    if (options.version) {
        persistConfig.version = options.version
    }

    if (options.migrations) {
        persistConfig.migrate = options.migrations
    }

    const persistedReducer = persistReducer(persistConfig, asyncReducer)

    injectUiReducer(key, persistedReducer)
}

export const buildRootReducer = (): Reducer<any, AnyAction> =>
    combineReducers({
        auth: persistReducer(
            authPersistConfig,
            auth as Reducer<any, AnyAction>
        ),
        orgs,
        ui: persistReducer(uiPersistConfig, buildReducer()),
        cache,
    })
